import React, { useState } from 'react';
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  Button,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { CompanyImage, CompanyImageType } from '../../../../types/models';
import { format } from 'date-fns';

interface CompanyImageCardProps {
  image: CompanyImage;
  onEdit: (image: CompanyImage) => void;
  onDelete: (image: CompanyImage) => void;
  onPreview: (image: CompanyImage) => void;
}

const ImageTypeColors: Record<CompanyImageType, 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'> = {
  [CompanyImageType.LOGO]: 'success',
  [CompanyImageType.HEADER]: 'success',
  [CompanyImageType.SIGNATURE]: 'warning',
  [CompanyImageType.WATERMARK]: 'info',
  [CompanyImageType.LETTERHEAD]: 'success',
  [CompanyImageType.OTHER]: 'secondary'
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const CompanyImageCard: React.FC<CompanyImageCardProps> = ({
  image,
  onEdit,
  onDelete,
  onPreview
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(image);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete(image);
    handleMenuClose();
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = image.url; // Updated from imageUrl
    link.download = image.fileName; // Updated from imageName
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleMenuClose();
  };

  const handlePreview = () => {
    onPreview(image);
    handleMenuClose();
  };

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3
        }
      }}
      onClick={() => onPreview(image)}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="200"
          image={image.url} // Updated from imageUrl
          alt={image.fileName} // Updated from imageName
          sx={{
            objectFit: 'contain',
            backgroundColor: '#f5f5f5',
            p: 1
          }}
        />
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <IconButton
            size="small"
            onClick={handleMenuClick}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)'
              }
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
        <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
          <Chip
            label={image.imageType}
            size="small"
            color={ImageTypeColors[image.imageType]}
            sx={{ textTransform: 'capitalize' }}
          />
        </Box>
      </Box>

      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Typography 
          variant="h6" 
          noWrap 
          sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}
          title={image.fileName} // Updated from imageName
        >
          {image.fileName} {/* Updated from imageName */}
        </Typography>
        
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {formatFileSize(image.fileSize)}
          </Typography>
          {image.width && image.height && ( // Updated from dimensions
            <Typography variant="body2" color="text.secondary">
              {image.width} × {image.height}
            </Typography>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" display="block">
          Uploaded by {image.uploadedByName || 'Unknown'}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {format(image.uploadedAt, 'MMM dd, yyyy')}
        </Typography>
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handlePreview}>
          <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
          Preview
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
          Download
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>
    </Card>
  );
};

interface CompanyImageGridProps {
  images: CompanyImage[];
  loading?: boolean;
  onEdit: (image: CompanyImage) => void;
  onDelete: (image: CompanyImage) => void;
  onPreview: (image: CompanyImage) => void;
}

export const CompanyImageGrid: React.FC<CompanyImageGridProps> = ({
  images,
  loading = false,
  onEdit,
  onDelete,
  onPreview
}) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)'
          },
          gap: 3
        }}
      >
        {Array.from({ length: 6 }).map((_, index) => (
          <Card key={index} sx={{ height: 300 }}>
            <Box
              sx={{
                height: 200,
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography color="text.secondary">Loading...</Typography>
            </Box>
            <CardContent>
              <Box sx={{ height: 80 }} />
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  if (images.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          textAlign: 'center',
          p: 4
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No company images found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload your first company logo or image to get started
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)'
        },
        gap: 3
      }}
    >
      {images.map((image) => (
        <CompanyImageCard
          key={image.id}
          image={image}
          onEdit={onEdit}
          onDelete={onDelete}
          onPreview={onPreview}
        />
      ))}
    </Box>
  );
};

interface EditImageDialogProps {
  image: CompanyImage | null;
  open: boolean;
  onClose: () => void;
  onSave: (imageId: string, updates: { fileName: string; imageType: CompanyImageType }) => void; // Updated from imageName
}

export const EditImageDialog: React.FC<EditImageDialogProps> = ({
  image,
  open,
  onClose,
  onSave
}) => {
  const [fileName, setFileName] = useState(''); // Updated from imageName
  const [imageType, setImageType] = useState<CompanyImageType>(CompanyImageType.LOGO);

  React.useEffect(() => {
    if (image) {
      setFileName(image.fileName); // Updated from imageName
      setImageType(image.imageType);
    }
  }, [image]);

  const handleSave = () => {
    if (image && fileName.trim()) { // Updated from imageName
      onSave(image.id, {
        fileName: fileName.trim(), // Updated from imageName
        imageType
      });
      onClose();
    }
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    setImageType(event.target.value as CompanyImageType);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Company Image</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {image && (
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={image.url} // Updated from imageUrl
                alt={image.fileName} // Updated from imageName
                style={{
                  maxWidth: '100%',
                  maxHeight: 200,
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
          
          <TextField
            fullWidth
            label="Image Name"
            value={fileName} // Updated from imageName
            onChange={(e) => setFileName(e.target.value)} // Updated from setImageName
            placeholder="Enter image name"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--accent-green)'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'var(--accent-green)'
              }
            }}
          />
          
          <FormControl 
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--accent-green)'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'var(--accent-green)'
              }
            }}
          >
            <InputLabel>Image Type</InputLabel>
            <Select
              value={imageType}
              label="Image Type"
              onChange={handleTypeChange}
            >
              {Object.values(CompanyImageType).map((type) => (
                <MenuItem key={type} value={type}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={type}
                      size="small"
                      color={ImageTypeColors[type]}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!fileName.trim()} // Updated from imageName
          sx={{
            backgroundColor: 'var(--accent-green)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'var(--accent-green)',
              filter: 'brightness(0.9)'
            },
            '&:disabled': {
              backgroundColor: 'rgba(0, 0, 0, 0.12)'
            }
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ImagePreviewDialogProps {
  image: CompanyImage | null;
  open: boolean;
  onClose: () => void;
}

export const ImagePreviewDialog: React.FC<ImagePreviewDialogProps> = ({
  image,
  open,
  onClose
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {image?.fileName} {/* Updated from imageName */}
        {image && (
          <Chip
            label={image.imageType}
            size="small"
            color={ImageTypeColors[image.imageType]}
            sx={{ textTransform: 'capitalize' }}
          />
        )}
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {image && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
              backgroundColor: '#f5f5f5',
              p: 2
            }}
          >
            <img
              src={image.url} // Updated from imageUrl
              alt={image.fileName} // Updated from imageName
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {image && (
          <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {formatFileSize(image.fileSize)} • 
                {image.width && image.height && ` ${image.width} × ${image.height}`} • 
                Uploaded {format(image.uploadedAt, 'MMM dd, yyyy')}
              </Typography>
            </Box>
            <Button onClick={onClose}>Close</Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};
